/* eslint-disable */
// /src/services/storeOrderService.js

import { db } from "./firebase";
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit as qlimit,
    updateDoc,
    serverTimestamp,
} from "firebase/firestore";

// 스토어 주문 컬렉션
const COL_NAME = "store_orders";
const COL_STORE_ORDERS = collection(db, COL_NAME);

// number 정규화 유틸
const toInt = (v) => {
    if (v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

function safeInt(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.trunc(n);
}

// 숫자만 남기는 전화번호 정규화
const normalizePhone = (v = "") => String(v).replace(/[^\d]/g, "");

/**
 * 멀티 아이템 주문 생성 (포인트/실결제(payableTotal) 포함)
 *
 * 추가(포인트):
 *   pointsUsed?: number
 *   payableTotal?: number
 *
 * - price.total: 원래 총액(상품+배송)
 * - price.payableTotal: 포인트 적용 후 실제 결제/입금 금액
 * - points.used: 사용 포인트
 * - payment.method: bank_transfer
 * - payment.pointsUsed: 사용 포인트
 */
export async function createStoreOrder(input) {
    if (!input) {
        throw new Error("createStoreOrder: payload is required");
    }

    const ownerPhoneE164 = input.ownerPhoneE164;
    if (!ownerPhoneE164) {
        throw new Error("createStoreOrder: ownerPhoneE164 is required");
    }

    const channel = input.channel || "store";
    const ordererRaw = input.orderer || {};
    const receiverRaw = input.receiver || {};
    const status = input.status || "pending_deposit";

    let items = [];

    // ✅ 1) 멀티 아이템 시그니처
    if (Array.isArray(input.items) && input.items.length > 0) {
        items = input.items.map((raw) => {
            const optionSelections = Array.isArray(raw.optionSelections)
                ? raw.optionSelections.map((op) => ({
                    groupName: op.groupName || "",
                    value: op.value || "",
                    extraPrice: toInt(op.extraPrice),
                }))
                : [];

            const basePrice = toInt(raw.unitPrice);
            const extraTotal = optionSelections.reduce((sum, op) => sum + toInt(op.extraPrice), 0);
            const qty = toInt(raw.qty || 1);
            const goodsSubtotal = (basePrice + extraTotal) * qty;
            const shippingFee = toInt(raw.shippingFeePerItem || raw.shippingFee || 0);

            return {
                productId: raw.productId || "",
                productTitle: raw.productTitle || raw.productTitleSnapshot || "",
                thumb: raw.thumb || "",
                unitPrice: basePrice,
                qty,
                optionSelections,
                goodsSubtotal,
                shippingFee,
            };
        });
    } else {
        // ✅ 2) 구버전 단일 아이템 시그니처
        const optionSelections = Array.isArray(input.optionSelections)
            ? input.optionSelections.map((op) => ({
                groupName: op.groupName || "",
                value: op.value || "",
                extraPrice: toInt(op.extraPrice),
            }))
            : [];

        const basePrice = toInt(input.unitPrice);
        const extraTotal = optionSelections.reduce((sum, op) => sum + toInt(op.extraPrice), 0);
        const qty = toInt(input.qty || 1);
        const goodsSubtotal = (basePrice + extraTotal) * qty;
        const shippingFee = toInt(input.shippingFee || 0);

        items = [
            {
                productId: input.productId || "",
                productTitle: input.productTitle || input.productTitleSnapshot || "",
                thumb: input.thumb || "",
                unitPrice: basePrice,
                qty,
                optionSelections,
                goodsSubtotal,
                shippingFee,
            },
        ];
    }

    if (!items.length) {
        throw new Error("createStoreOrder: items is empty");
    }

    // ✅ 금액 집계
    const itemsTotal = items.reduce((sum, it) => sum + toInt(it.goodsSubtotal), 0);

    let shippingTotal;
    if (typeof input.shippingTotal === "number") {
        shippingTotal = toInt(input.shippingTotal);
    } else {
        shippingTotal = items.reduce((sum, it) => sum + toInt(it.shippingFee), 0);
    }

    const total = itemsTotal + shippingTotal;

    // ======================================================
    // ✅ 포인트/실결제: "어디로 오든" 읽어오게 방어 처리
    //  - 우선순위: input.pointsUsed -> input.pricing.pointsUsed -> input.payment.pointsUsed -> 0
    //  - payableTotal도: input.payableTotal -> input.pricing.payablePrice/payableTotal -> 계산
    // ======================================================
    const pointsUsedRaw =
        input?.pointsUsed ??
        input?.pricing?.pointsUsed ??
        input?.payment?.pointsUsed ??
        0;

    const pointsUsed = Math.max(0, safeInt(pointsUsedRaw));

    const payableRaw =
        input?.payableTotal ??
        input?.pricing?.payablePrice ??
        input?.pricing?.payableTotal ??
        null;

    const payableTotal =
        typeof payableRaw === "number"
            ? Math.max(0, safeInt(payableRaw))
            : Math.max(0, total - pointsUsed);

    // ✅ 주문자/수령자 정제
    const ordererPhoneNorm = normalizePhone(ordererRaw.phone);
    const receiverPhoneNorm = normalizePhone(receiverRaw.phone);

    const orderer = {
        name: ordererRaw.name || "",
        phone: ordererPhoneNorm || "",
        email: ordererRaw.email || "",
        zip: ordererRaw.zip || "",
        addr1: ordererRaw.addr1 || "",
        addr2: ordererRaw.addr2 || "",
    };

    const receiver = {
        name: receiverRaw.name || "",
        phone: receiverPhoneNorm || "",
        zip: receiverRaw.zip || "",
        addr1: receiverRaw.addr1 || "",
        addr2: receiverRaw.addr2 || "",
        memo: receiverRaw.memo || "",
    };

    const first = items[0];
    const productTitleSnapshot =
        items.length > 1
            ? `${first.productTitle || ""} 외 ${items.length - 1}건`
            : first.productTitle || "";

    const now = serverTimestamp();

    const docData = {
        ownerPhoneE164,
        channel,
        items,

        price: {
            itemsTotal,
            shippingTotal,
            total,
            payableTotal,
        },

        points: {
            used: pointsUsed,
        },

        payment: {
            method: "bank_transfer",
            pointsUsed: pointsUsed,
            // ✅ 혹시 화면에서 쓰면 도움됨 (선택)
            payableTotal: payableTotal,
        },

        // ✅ (테스트 편의) 화면에서 바로 쓰기 쉬운 요약 값도 하나 더 둬도 됨
        pointsUsed: pointsUsed,
        payableTotal: payableTotal,

        orderer,
        receiver,
        status,
        createdAt: now,
        updatedAt: now,

        // ✅ 기존 화면 호환용
        productId: first.productId || "",
        productTitleSnapshot,
        unitPrice: first.unitPrice,
        qty: first.qty,
        shippingFee: shippingTotal,
        totalPrice: total,
    };

    const ref = await addDoc(COL_STORE_ORDERS, docData);
    return { orderId: ref.id };
}


/**
 * 단건 조회
 */
export async function getStoreOrder(orderId) {
    if (!orderId) return null;
    const snap = await getDoc(doc(db, COL_NAME, orderId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * 사용자(드라이버) 기준 주문 목록 조회
 *  - 기본: 최신순, limitCount 개
 */
export async function listStoreOrdersByOwnerPhoneE164(ownerPhoneE164, options = {}) {
    if (!ownerPhoneE164) return [];
    const limitCount = options.limitCount || 100;

    const q = query(
        COL_STORE_ORDERS,
        where("ownerPhoneE164", "==", ownerPhoneE164),
        orderBy("createdAt", "desc"),
        qlimit(limitCount)
    );

    const snap = await getDocs(q);
    const out = [];
    snap.forEach((docSnap) => {
        out.push({ id: docSnap.id, ...docSnap.data() });
    });
    return out;
}

/**
 * 주문자 휴대폰 기준 목록 조회 (정보성)
 */
export async function listStoreOrdersByPhone(phone, { limitCount = 50 } = {}) {
    const norm = normalizePhone(phone);
    if (!norm) return [];

    const q = query(
        COL_STORE_ORDERS,
        where("orderer.phone", "==", norm),
        orderBy("createdAt", "desc"),
        qlimit(limitCount)
    );

    const snap = await getDocs(q);
    const out = [];
    snap.forEach((docSnap) => {
        out.push({ id: docSnap.id, ...docSnap.data() });
    });
    return out;
}

/**
 * 관리자용: 전체 주문 목록 조회
 *  - 기본: 최신순, limitCount 개
 */
export async function listStoreOrdersForAdmin(options = {}) {
    const limitCount = options.limitCount || 300;

    const q = query(COL_STORE_ORDERS, orderBy("createdAt", "desc"), qlimit(limitCount));

    const snap = await getDocs(q);
    const out = [];
    snap.forEach((docSnap) => {
        out.push({ id: docSnap.id, ...docSnap.data() });
    });
    return out;
}

/**
 * 주문 상태만 변경 (관리자/사용자 공용)
 */
export async function updateStoreOrderStatus(orderId, nextStatus) {
    if (!orderId) throw new Error("updateStoreOrderStatus: orderId is required");
    const ref = doc(COL_STORE_ORDERS, orderId);
    await updateDoc(ref, {
        status: nextStatus,
        updatedAt: serverTimestamp(),
    });
}

/**
 * 주문 문서 patch 업데이트 (주문자/수령자/배송정보 등)
 *  - patch는 그대로 merge되며, updatedAt 자동 갱신
 */
export async function updateStoreOrder(orderId, patch) {
    if (!orderId) throw new Error("updateStoreOrder: orderId is required");
    if (!patch || typeof patch !== "object") return;

    const ref = doc(COL_STORE_ORDERS, orderId);
    await updateDoc(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
    });
}
